import { SOURCE_TOGGLE } from 'store/eventConstants';
import setSourceUploadStatus from '@shoflo/studio-redux/actions/source/setSourceUploadStatus';
import { logProgramChange } from '@shoflo/studio-redux/utils/sendToDatadogWithIds';
import { UPLOADED } from 'constants/SourceUploadStatus';
import { getSignedUrl } from '../../../utils/broadcastSourceHelper';
import { withCurrentParams } from 'actions/utils';
import setProgram from '../program/setProgram';
import updateScenesPreview from '../scene/updateScenesPreview';
import { pick } from 'underscore';
import { IMAGE_FILE } from '@shoflo/polymer/constants/SourceItems';
import { BROADCAST_UPDATE_CONTENT_SOURCE_STATUS } from '@shoflo/studio-redux/actions/source/types';

/**
 * It updates the signed url for the source modified by {@link updateContentSource} which will be fetched using s3 proxy once upload is successful.
 * Additionally, it hides the UploadStatusPane and fires a message on socket to notify other directors about the successful update of the source
 * It also updates the program and displays the uploaded content to the live screen preview accordingly
 * @param source - The actual source media which was uploaded and for which the state should be updated
 */
const updateContentSourceStatus = source => {
  return withCurrentParams((dispatch, { broadcastId }, getState, socket) => {
    const sourceId = source._id;
    const action = {
      type: BROADCAST_UPDATE_CONTENT_SOURCE_STATUS,
      request: async apiClient => {
        const { title: fileName = '' } = source || {};
        let updatedSource = await getSignedUrl(source, apiClient, true);
        const shouldUpdateScenePreview = source.type === IMAGE_FILE;
        dispatch(setSourceUploadStatus(fileName, UPLOADED));
        // Get state as late as possible to fetch the most recent version of liveSources and program.
        // This reduces or eliminates the possibility of a program update that overrides a newer version
        const {
          meeting: { attendeeResponse },
          program
        } = getState();

        // This ensures we only update the program with source when we are not trying to show or hide it
        // What changes should make us update program? it's usually the source value
        const sourceInProgram = program.layers.find(
          layer => layer?.source?._id === sourceId
        );

        if (sourceInProgram) {
          const updatedProgram = {
            ...program,
            layers: program.layers.map(layer => {
              const { source: layerSource } = layer;
              // Actual source that was updated
              if (layerSource._id === sourceId) {
                return {
                  ...layer,
                  source: {
                    ...updatedSource
                  }
                };
              }
              if (
                layerSource.type === 'banner' &&
                layerSource?._id?.split('_').reverse()[0] === updatedSource?._id
              ) {
                return {
                  ...layer,
                  source: {
                    ...layerSource,
                    value: {
                      ...layerSource.value,
                      templateVars: {
                        ...layerSource.value.templateVars,
                        LINE_1: updatedSource.title,
                        LINE_2: updatedSource.description || ''
                      }
                    }
                  }
                };
              }

              return layer;
            })
          };

          dispatch(setProgram(updatedProgram));
          logProgramChange(getState, 'updateContentSourceStatus action');
        }

        // notify other directors
        socket.emit(SOURCE_TOGGLE, {
          broadcastId,
          notifyingDirector: attendeeResponse?.Attendee?.AttendeeId,
          source: updatedSource
        });

        if (shouldUpdateScenePreview) {
          const sourceSceneUpdate = {
            ...pick(updatedSource, ['value', 'type'])
          };
          if (sourceInProgram) {
            // We want this to run a bit after update program so as not to throw
            // a mongo error for version control on the broadcast object
            setTimeout(() => {
              dispatch(updateScenesPreview(sourceId, null, sourceSceneUpdate));
            }, 3000);
          } else {
            dispatch(updateScenesPreview(sourceId, null, sourceSceneUpdate));
          }
        }

        return updatedSource;
      }
    };
    dispatch(action);
  });
};

export default updateContentSourceStatus;
